import { Controller } from "@hotwired/stimulus"

export class ApplicantCustomFieldMetadataError extends Error {
  constructor(message) {
    super(message)
    this.name = "ApplicantCustomFieldMetadataError"
  }
}

// Rails supplies comparison values and native-required eligibility. Hidden
// answers remain successful controls; the server still validates their values.
export default class extends Controller {
  static targets = ["field"]

  connect() {
    this.refresh()
  }

  refresh() {
    const form = this.element.closest("form")
    if (!form) throw new ApplicantCustomFieldMetadataError("Applicant fields need their containing form")

    const submitted = new FormData(form)
    // Read every row before changing affordances, so malformed metadata is an
    // explicit error rather than a partially applied applicability decision.
    const states = this.fieldTargets.map((row) => {
      const condition = this.conditionFor(row)
      const controls = Array.from(row.querySelectorAll("[data-applicant-custom-fields-required-eligible]"))
      if (controls.length === 0) {
        throw new ApplicantCustomFieldMetadataError("A conditional row needs its value control")
      }
      const eligible = controls.map((control) => {
        const value = control.dataset.applicantCustomFieldsRequiredEligible
        if (value !== "true" && value !== "false") {
          throw new ApplicantCustomFieldMetadataError("Invalid native-required eligibility")
        }
        return value === "true"
      })
      const values = submitted.getAll(condition.parent_name)
      const actual = condition.parent_cardinality === "array" ? values : values[values.length - 1]
      // Array values stay arrays. In particular, they are never joined into a
      // scalar that could accidentally match Rails' expected String.
      const applicable = values.length > 0 && actual === condition.expected_value
      return { row, controls, eligible, applicable, required: condition.required }
    })

    states.forEach(({ row, controls, eligible, applicable, required }) => {
      row.hidden = !applicable
      controls.forEach((control, index) => {
        control.required = applicable && required && eligible[index]
      })
    })
  }

  conditionFor(row) {
    let condition
    try {
      condition = JSON.parse(row.dataset.applicantCustomFieldsCondition)
    } catch (_error) {
      throw new ApplicantCustomFieldMetadataError("Missing or invalid conditional row JSON")
    }
    if (!condition || Array.isArray(condition) || typeof condition !== "object" ||
        typeof condition.parent_name !== "string" || condition.parent_name.length === 0 ||
        !["scalar", "array"].includes(condition.parent_cardinality) ||
        !Object.prototype.hasOwnProperty.call(condition, "expected_value") ||
        !(condition.expected_value === null || ["string", "number", "boolean"].includes(typeof condition.expected_value)) ||
        typeof condition.required !== "boolean") {
      throw new ApplicantCustomFieldMetadataError("Invalid conditional row metadata")
    }
    return condition
  }
}
